import type {
  ImmutableProgram,
  InitialProperties,
  ImmutableResult,
} from '#lit/components/mixins/immutable_component/index.js';
import {
  Variant,
  makeVariants,
} from '#lit/components/mixins/immutable_component/index.js';
import type {VariantsFor} from '#lit/types/architecture.js';
import {html, nothing} from 'lit';
import {ref} from 'lit/directives/ref.js';
import type SplitButton from './index.js';
import {Size as ButtonSize} from '#lit/components/base/button/index.js';
import {when} from 'lit/directives/when.js';
import {isHTMLElement} from '#lit/utils/dom.js';
import type FlyListItem from '../list_item/index.js';

export type Model = {
  visible: boolean;
};

export const Msg = makeVariants({
  open: Variant<void>()('open'),
  close: Variant<void>()('close'),
  toggle: Variant<void>()('toggle'),
});

export type Msg = VariantsFor<typeof Msg>;

// -- Init

function init(props: InitialProperties): ImmutableResult<Model> {
  return {
    visible: <boolean>props.get('boolean') ?? false,
  };
}

function update(msg: Msg, model: Model): ImmutableResult<Model> {
  switch (msg.tag) {
    case 'open':
      return {...model, visible: true};
    case 'close':
      return {...model, visible: false};
    case 'toggle':
      return {...model, visible: !model.visible};
  }
}

function view(this: SplitButton, _model: Readonly<Model>) {
  return html`
    <div class="wrapper">
      <button
        ${ref(this.btnRef)}
        type="button"
        label="Node options"
        class="node-options"
        @click=${(event: MouseEvent) => {
          event.stopPropagation();
          this.handleUpdate(Msg.toggle());
        }}
      >
        <fly-icon size=${ButtonSize.SM} icon="more_vert"></fly-icon>
      </button>
      ${when(
        this.btnRef,
        () => html`
          <fly-floating-surface-v2
            placement=${this.placement}
            closeWhenClickOutside
            .anchor=${this.btnRef.value ?? null}
            ?visible=${this.model.visible}
            @close=${() => this.handleUpdate(Msg.close)}
            @open=${() => this.handleUpdate(Msg.open)}
            @click=${(event: MouseEvent) => {
              event.stopPropagation();
            }}
          >
            <fly-list>
              <slot
                @click=${(event: MouseEvent) => {
                  for (const el of event.composedPath()) {
                    if (!isHTMLElement(el)) continue;
                    if (el.tagName.toLocaleLowerCase() === 'fly-list-item') {
                      (el as FlyListItem).selected = false;
                      this.handleUpdate(Msg.close());
                      return;
                    }
                  }
                }}
              ></slot>
            </fly-list>
          </fly-floating-surface-v2>
        `,
        () => nothing
      )}
    </div>
  `;
}

// -- Program

export const main: ImmutableProgram<Model, Msg> = {
  init,
  update,
  view,
};
